import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";

const HeroSection: React.FC = () => {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const { ref, inView } = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      setMousePosition({
        x: (e.clientX / window.innerWidth) * 100,
        y: (e.clientY / window.innerHeight) * 100,
      });
    };

    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, []);

  return (
    <section className="relative min-h-screen flex items-center overflow-hidden dark-gradient">
      {/* Animated Background */}
      <div className="absolute inset-0">
        <div
          className="absolute inset-0 bg-gradient-to-br from-dark-900 via-dark-800 to-primary-900 opacity-90"
          style={{
            transform: `translate(${mousePosition.x * 0.02}px, ${
              mousePosition.y * 0.02
            }px)`,
          }}
        />
        <div className="absolute inset-0 bg-black/30" />

        {/* Floating Elements */}
        <motion.div
          className="absolute top-20 left-10 w-20 h-20 bg-primary-500/20 rounded-full blur-xl"
          animate={{
            y: [0, -20, 0],
            x: [0, 10, 0],
          }}
          transition={{
            duration: 6,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
        <motion.div
          className="absolute top-40 right-20 w-32 h-32 bg-secondary-500/20 rounded-full blur-xl"
          animate={{
            y: [0, 20, 0],
            x: [0, -15, 0],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
        <motion.div
          className="absolute bottom-40 left-1/4 w-16 h-16 bg-white/10 rounded-full blur-lg"
          animate={{
            y: [0, -15, 0],
            x: [0, 20, 0],
          }}
          transition={{
            duration: 7,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
      </div>

      {/* Content */}
      <div
        ref={ref}
        className="relative z-10 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto w-full"
      >
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div className="text-left">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight">
                Employee Tracking
                <span className="block gradient-text">Made Easy</span>
              </h1>
            </motion.div>

            <motion.p
              className="text-xl sm:text-2xl text-white/90 mb-8 max-w-2xl leading-relaxed"
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              Desktop app with web portal for admins and employees to track
              progress and productivity. High-end analytics that help
              organizations make better decisions.
            </motion.p>

            <motion.div
              className="flex flex-col sm:flex-row gap-4 items-start mb-10"
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.6 }}
            >
              <button className="btn-secondary text-lg px-8 py-4">
                Join Waitlist - 1 Month Free
              </button>
            </motion.div>

            {/* Waitlist Stats */}
            <motion.div
              className="max-w-md"
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.8 }}
            >
              <div className="p-6 border border-white/10 rounded-xl">
                <div className="flex items-center gap-3 mb-2">
                  <svg
                    className="w-6 h-6 text-white/90"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M17 21v-2a4 4 0 0 0-4-4H7a4 4 0 0 0-4 4v2"></path>
                    <circle cx="9" cy="7" r="4"></circle>
                    <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                    <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                  </svg>
                  <div className="text-4xl font-extrabold text-white tracking-tight">
                    100+
                  </div>
                </div>
                <div className="text-white/90 font-semibold">
                  People Joined the Waitlist
                </div>
                <div className="flex items-center -space-x-3 mt-3">
                  <div className="w-8 h-8 rounded-full bg-white/80 border border-white/30 flex items-center justify-center text-dark-900 text-[10px] font-semibold">
                    AK
                  </div>
                  <div className="w-8 h-8 rounded-full bg-white/80 border border-white/30 flex items-center justify-center text-dark-900 text-[10px] font-semibold">
                    JS
                  </div>
                  <div className="w-8 h-8 rounded-full bg-white/80 border border-white/30 flex items-center justify-center text-dark-900 text-[10px] font-semibold">
                    MP
                  </div>
                  <div className="w-8 h-8 rounded-full bg-white/80 border border-white/30 flex items-center justify-center text-dark-900 text-[10px] font-semibold">
                    ZT
                  </div>
                  <div className="w-8 h-8 rounded-full bg-primary-600 text-white border border-white/30 text-[10px] font-semibold">
                    +
                  </div>
                </div>
                <div className="text-white/70 text-xs mt-2">
                  Be among the first to get 1 month free
                </div>
              </div>
            </motion.div>
          </div>

          {/* Right visual preview */}
          <motion.div
            className="relative transform scale-105"
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.5 }}
          >
            <motion.div
              className="glass-card p-6 rounded-2xl will-change-transform"
              animate={{ rotate: [-2, 2, -2] }}
              transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
            >
              <div className="flex items-center mb-4 gap-3">
                <div className="h-3 w-3 rounded-full bg-red-400" />
                <div className="h-3 w-3 rounded-full bg-yellow-400" />
                <div className="h-3 w-3 rounded-full bg-green-400" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-white/10 rounded-xl p-4 text-white">
                  <div className="text-sm opacity-80">Productivity</div>
                  <div className="text-2xl font-bold">87%</div>
                </div>
                <div className="bg-white/10 rounded-xl p-4 text-white">
                  <div className="text-sm opacity-80">Focus Time</div>
                  <div className="text-2xl font-bold">6.2h</div>
                </div>
                <div className="col-span-2 bg-white/10 rounded-xl p-6 h-40 flex items-center justify-center border border-white/10">
                  <div className="text-white/80">Interactive Chart</div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <motion.div
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
        animate={{ y: [0, 10, 0] }}
        transition={{ duration: 2, repeat: Infinity }}
      >
        <div className="w-6 h-10 border-2 border-white/50 rounded-full flex justify-center">
          <motion.div
            className="w-1 h-3 bg-white/70 rounded-full mt-2"
            animate={{ y: [0, 12, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
          />
        </div>
      </motion.div>
    </section>
  );
};

export default HeroSection;
